/** Shared building blocks for the dedicated search view. */
import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import DarkSelect from "../home/DarkSelect";

export const fmtEur = (n) => `${Number(n).toLocaleString("de-AT")} €`;
export const fmtKm = (n) => `${Number(n).toLocaleString("de-AT")} km`;

/** Collapsible criteria group — plain language title + active count badge. */
export function Section({ title, count = 0, defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-2xl border border-zinc-800 bg-zinc-900/40">
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="text-sm font-semibold text-zinc-200">
          {title}
          {count > 0 && (
            <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
              {count}
            </span>
          )}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && <div className="grid grid-cols-1 gap-3 border-t border-zinc-800/70 p-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>}
    </section>
  );
}

export function Field({ label, children, testid }) {
  return (
    <label className="block space-y-1.5" data-testid={testid}>
      <span className="text-xs font-medium text-zinc-400">{label}</span>
      {children}
    </label>
  );
}

/** Sensible steps + optional custom value (never free text for canonicals). */
export function NumberSelect({ value, onChange, steps = [], anyLabel, customLabel, format = (n) => String(n), testid }) {
  const inSteps = value == null || steps.includes(value);
  const [custom, setCustom] = useState(!inSteps);
  const options = [
    { value: "", label: anyLabel },
    ...steps.map((s) => ({ value: s, label: format(s) })),
    { value: "__custom", label: customLabel },
  ];
  if (custom) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          autoFocus
          data-testid={testid ? `${testid}-custom` : undefined}
          className="w-full rounded-xl border border-emerald-600/50 bg-zinc-900 px-3 py-2.5 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
          value={value ?? ""}
          placeholder={anyLabel}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange(e.target.value === "" || !Number.isFinite(n) ? null : n);
          }}
        />
        <button
          type="button"
          className="text-xs text-zinc-500 hover:text-zinc-300"
          onClick={() => { setCustom(false); }}
        >
          ✕
        </button>
      </div>
    );
  }
  return (
    <DarkSelect
      value={value ?? ""}
      testid={testid}
      options={options}
      onChange={(v) => {
        if (v === "__custom") setCustom(true);
        else onChange(v === "" ? null : v);
      }}
    />
  );
}

/** Toggle chip (tri-state safe: only explicit ON filters). */
export function ToggleChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={!!active}
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
        active
          ? "border-emerald-500/70 bg-emerald-500/15 text-emerald-300"
          : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
      }`}
    >
      {children}
    </button>
  );
}
